"use client";

import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Clipboard, MilkIcon as Cow, Tag } from "lucide-react";

import { animateChildren } from "@/lib/animation-utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { nigerianBreeds } from "@/utils/items.ts";

export default function IdentificationStep() {
  const { control, formState: { dirtyFields } } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        (child as HTMLElement).style.opacity = "0";
        (child as HTMLElement).style.transform = "translateY(20px)";
      });

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          );
        }
      });
    }
  }, []);

  return (
    <div ref={containerRef} className="space-y-6 z-50">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">
          Cow Identification
        </h2>
        <p className="text-slate-500">
          Enter the basic identification details for the cow
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="cowEarTag"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Tag className="h-4 w-4" />
                Cow Ear Tag
                {dirtyFields.cowEarTag && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter ear tag number" {...field} />
              </FormControl>
              {dirtyFields.cowEarTag && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="cowManagement"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Clipboard className="h-4 w-4" />
                Cow Management
                {dirtyFields.cowManagement && (
                   <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input placeholder="Management identifier" {...field} />
              </FormControl>
              {dirtyFields.cowManagement && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="cowBreed"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Cow className="h-4 w-4" />
                Cow Breed
                {dirtyFields.cowBreed && (
                   <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value} onValueChange={(value) => {
                // The native select can emit an empty value while saved options mount.
                if (value) field.onChange(value);
              }}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select breed" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {field.value && !nigerianBreeds.some(breed => breed.breed === field.value) && (
                    <SelectItem value={field.value}>{field.value}</SelectItem>
                  )}
                  {nigerianBreeds.map((breed) => (
                    <SelectItem
                      key={breed.label}
                      className={"overflow-y-auto z-50 background-white"}
                      value={breed.breed}
                    >
                      {breed.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.cowBreed && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
